"use client";

import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { clinicAPI, adminAPI, handleApiError } from "../../services/api";
import ClinicReviewCard from "./components/ClinicReviewCard";
import ClinicDetailModal from "./components/ClinicDetailModal";
import SystemAnnouncementComposer from "./components/SystemAnnouncementComposer";
import BulkMessageComposer from "./components/BulkMessageComposer";
import AppBannerManager from "./components/AppBannerManager";
import BreedManager from "./components/BreedManager";
import SpeciesManager from "./components/SpeciesManager";
import AdvertisementManager from "./components/AdvertisementManager";
import "./AdminDashboard.css";

const AdminDashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { getUserType, logout } = useAuth();

  const [clinics, setClinics] = useState([]);
  const [allClinics, setAllClinics] = useState([]); // Store all filtered clinics for pagination
  const [totalClinics, setTotalClinics] = useState([]); // Store ALL clinics (unfiltered) for stats
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, verified, rejected, new (unverified), upgrade-requests - default to "all" to show all clinics
  const [isCmsOpen, setIsCmsOpen] = useState(false);
  const [isBreedsSubOpen, setIsBreedsSubOpen] = useState(false);
  const [isSpeciesSubOpen, setIsSpeciesSubOpen] = useState(false);
  const [isBannerSubOpen, setIsBannerSubOpen] = useState(false);
  const [isAdsSubOpen, setIsAdsSubOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const clinicsPerPage = 9;
  const isLoadingRef = useRef(false);

  // Sync state with URL
  useEffect(() => {
    const path = location.pathname;
    let newFilter = "all";

    if (path === "/admin/dashboard/announcements") newFilter = "announcements";
    else if (path === "/admin/dashboard/bulk-messages") newFilter = "bulk_messages";
    else if (path === "/admin/dashboard/paid") newFilter = "paid";
    else if (path === "/admin/dashboard/cms/banners") {
      newFilter = "cms_banners";
      setIsCmsOpen(true);
      setIsBannerSubOpen(true);
    }
    else if (path === "/admin/dashboard/cms/banners/add") {
      newFilter = "cms_banners_add";
      setIsCmsOpen(true);
      setIsBannerSubOpen(true);
    }
    else if (path.startsWith("/admin/dashboard/cms/banners/edit/")) {
      newFilter = "cms_banners_edit";
      setIsCmsOpen(true);
      setIsBannerSubOpen(true);
    }
    else if (path === "/admin/dashboard/cms/breeds") {
      newFilter = "cms_breeds_list";
      setIsCmsOpen(true);
      setIsBreedsSubOpen(true);
    }
    else if (path === "/admin/dashboard/cms/breeds/add") {
      newFilter = "cms_breeds_add";
      setIsCmsOpen(true);
      setIsBreedsSubOpen(true);
    }
    else if (path === "/admin/dashboard/cms/advertisements/add" || 
             (path.startsWith("/admin/dashboard/cms/advertisements/") && path.endsWith("/add"))) {
       newFilter = "cms_ads_add";
       setIsCmsOpen(true);
       setIsAdsSubOpen(true);
    }
    else if (path.startsWith("/admin/dashboard/cms/advertisements/edit/")) {
       newFilter = "cms_ads_edit";
       setIsCmsOpen(true);
       setIsAdsSubOpen(true);
    }
    else if (path.includes("/edit/") && path.startsWith("/admin/dashboard/cms/advertisements/")) {
      // Matches /admin/dashboard/cms/advertisements/:countryCode/edit/:adId
      newFilter = "cms_ads_edit";
      setIsCmsOpen(true);
      setIsAdsSubOpen(true);
    }
    else if (path === "/admin/dashboard/cms/advertisements" || path.startsWith("/admin/dashboard/cms/advertisements/")) {
      newFilter = "cms_ads_list";
      setIsCmsOpen(true);
      setIsAdsSubOpen(true);
    }
    else if (path === "/admin/dashboard/cms/species") {
      newFilter = "cms_species_list";
      setIsCmsOpen(true);
      setIsSpeciesSubOpen(true);
    }
    else if (path === "/admin/dashboard/cms/species/add") {
      newFilter = "cms_species_add";
      setIsCmsOpen(true);
      setIsSpeciesSubOpen(true);
    }

    else if (path === "/admin/dashboard/clinics/verified") newFilter = "verified";
    else if (path === "/admin/dashboard/clinics/rejected") newFilter = "rejected";
    else if (path === "/admin/dashboard/clinics/new") newFilter = "new";
    else if (path === "/admin/dashboard/clinics/upgrade-requests") newFilter = "upgrade-requests";
    else if (path === "/admin/dashboard" || path === "/admin/dashboard/") newFilter = "all";

    setFilter(newFilter);
  }, [location.pathname]);

  // Ensure body overflow is restored when component mounts (fix for scroll issues)
  useEffect(() => {
    // Restore body overflow on mount
    document.body.style.overflow = '';
    document.body.style.overflowY = '';

    // Cleanup: ensure overflow is restored on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.overflowY = '';
    };
  }, []);

  // Check user type on mount only
  useEffect(() => {
    const userType = getUserType();
    const isAdmin = userType === 'ADMIN';
    const isContentAdmin = userType === 'CONTENT_ADMIN';

    if (!isAdmin && !isContentAdmin) {
      navigate('/auth/login');
      return;
    }

    // Redirect CONTENT_ADMIN if they land on the base dashboard or clinic-related paths
    if (isContentAdmin && (
      location.pathname === '/admin/dashboard' || 
      location.pathname === '/admin/dashboard/' ||
      location.pathname.includes('/clinics/') ||
      location.pathname === '/admin/dashboard/announcements' ||
      location.pathname === '/admin/dashboard/paid'
    )) {
      setIsLoading(false);
      navigate('/admin/dashboard/cms/banners');
    } else if (isContentAdmin) {
      setIsLoading(false);
    }
  }, [navigate, location.pathname]);

  const loadClinics = async () => {
    if (isLoadingRef.current) return; // Prevent duplicate calls
    isLoadingRef.current = true;
    setIsLoading(true);
    try {
      // Use existing clinic API to fetch all clinics
      const response = await clinicAPI.getClinics(searchQuery || '');

      // Handle different response structures
      let clinicsData = [];
      if (response.data) {
        // Check if response.data.data exists (nested structure)
        if (response.data.data) {
          // Check if it's an object with clinics array
          if (response.data.data.clinics && Array.isArray(response.data.data.clinics)) {
            clinicsData = response.data.data.clinics;
          }
          // Check if it's directly an array
          else if (Array.isArray(response.data.data)) {
            clinicsData = response.data.data;
          }
          // Check if it's an object (single clinic)
          else if (typeof response.data.data === 'object') {
            clinicsData = [response.data.data];
          }
        }
        // Check if response.data is directly an array
        else if (Array.isArray(response.data)) {
          clinicsData = response.data;
        }
        // Check if response.data is an object with clinics property
        else if (response.data.clinics && Array.isArray(response.data.clinics)) {
          clinicsData = response.data.clinics;
        }
      }

      // Add default status if missing (default to 'unverified' for new clinics)
      // Check for various possible fields: status, verification_status, is_verified, verified
      const clinicsWithStatus = clinicsData.map(clinic => {
        let status = clinic.status || clinic.verification_status;

        // If status doesn't exist, check other possible fields
        if (!status) {
          if (clinic.is_verified === true || clinic.verified === true) {
            status = 'verified';
          } else {
            status = 'unverified'; // Default to unverified if no status found
          }
        }

        return {
          ...clinic,
          status: status
        };
      });

      // Store ALL clinics (unfiltered) for stats - this shows total overall
      setTotalClinics(clinicsWithStatus);

      // Filter by status
      let filtered = clinicsWithStatus;
      if (filter === "new") {
        filtered = clinicsWithStatus.filter(c => c.status === "unverified");
      } else if (filter === "verified") {
        filtered = clinicsWithStatus.filter(c => c.status === "verified");
      } else if (filter === "rejected") {
        filtered = clinicsWithStatus.filter(c => c.status === "rejected");
      } else if (filter === "upgrade-requests") {
        // Filter for clinics that have requested upgrade (upgrade_requested flag is true)
        filtered = clinicsWithStatus.filter(c => {
          if (c.status !== "verified") return false;

          // Check if clinic has requested upgrade
          const hasUpgradeRequest =
            c.clinic_subscription?.upgrade_requested === true ||
            c.clinic_subscription?.has_upgrade_request === true ||
            c.upgrade_requested === true ||
            c.has_upgrade_request === true;

          // Only show clinics that have requested upgrade and are not already paid
          if (!hasUpgradeRequest) return false;

          // Check subscription plan - don't show if already paid
          const subscriptionPlan =
            c.clinic_subscription?.plan ||
            c.clinic_subscription_plan;

          return subscriptionPlan !== 'paid';
        });
      } else if (filter === "paid") {
        // Filter for clinics that are verified and on paid plan
        // Backend provides: clinic.clinic_subscription.plan or clinic.clinic_subscription_plan
        filtered = clinicsWithStatus.filter(c => {
          if (c.status !== "verified") return false;

          // Check new clinicsubscription table structure
          const subscriptionPlan =
            c.clinic_subscription?.plan ||
            c.clinic_subscription_plan;

          // Only show clinics with 'paid' plan
          return subscriptionPlan === 'paid';
        });
      }
      // If filter is "all", show all clinics (no filtering)

      // Store filtered clinics for pagination
      setAllClinics(filtered);

      // Reset to page 1 when filter or search changes
      setCurrentPage(1);

      // Apply pagination
      const startIndex = (currentPage - 1) * clinicsPerPage;
      const endIndex = startIndex + clinicsPerPage;
      const paginatedClinics = filtered.slice(startIndex, endIndex);

      setClinics(paginatedClinics);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  };

  // Combined effect for filter and search query changes
  useEffect(() => {
    const userType = getUserType();
    if (userType !== 'ADMIN') {
      return;
    }

    // Only load clinics if on a clinics-related tab
    const clinicFilters = ["all", "new", "verified", "rejected", "upgrade-requests", "paid"];
    if (!clinicFilters.includes(filter)) {
      return;
    }

    if (searchQuery === undefined) return;

    const timeoutId = setTimeout(() => {
      loadClinics();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [filter, searchQuery]); // Only depend on filter and searchQuery

  // Handle pagination when page changes
  useEffect(() => {
    const startIndex = (currentPage - 1) * clinicsPerPage;
    const endIndex = startIndex + clinicsPerPage;
    const paginatedClinics = allClinics.slice(startIndex, endIndex);
    setClinics(paginatedClinics);
  }, [currentPage, allClinics]);

  const handleVerifyClinic = async (clinicId) => {
    try {
      await adminAPI.verifyClinic(clinicId, 'verified');

      // Reload clinics from API to get updated status
      await loadClinics();

      setShowDetailModal(false);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleRejectClinic = async (clinicId, reason) => {
    try {
      // Rejecting sets clinic status to 'rejected'
      await adminAPI.verifyClinic(clinicId, 'rejected', reason || null);

      // Reload clinics from API to get updated status
      await loadClinics();

      setShowDetailModal(false);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleUpgradeSubscription = async (clinicId) => {
    try {
      await adminAPI.upgradeClinicSubscription(clinicId);

      // Reload clinics from API to get updated subscription status
      await loadClinics();

      setShowDetailModal(false);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleDowngradeSubscription = async (clinicId) => {
    try {
      await adminAPI.downgradeClinicSubscription(clinicId);

      // Reload clinics from API to get updated subscription status
      await loadClinics();

      setShowDetailModal(false);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleViewDetails = (clinic) => {
    setSelectedClinic(clinic);
    setShowDetailModal(true);
  };

  const scrollToTop = () => {
    requestAnimationFrame(() => {
      const container = document.querySelector(".admin-dashboard-page");
      if (container && typeof container.scrollIntoView === "function") {
        container.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      }
    });
  };

  // Clinics are already paginated and filtered in loadClinics
  const filteredClinics = clinics;

  if (isLoading && clinics.length === 0) {
    return (
      <div className="admin-dashboard-page">
        <div className="admin-dashboard-loading">
          <div className="loading-spinner"></div>
          <p>{t("adminDashboard.loading")}</p>
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/auth/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <div className="admin-dashboard-page" dir={i18n.dir()}>
      {/* Top Header with Logout */}
      <div className="admin-dashboard-header-bar">
        <div className="admin-dashboard-header-bar-content">
          <h2 className="admin-dashboard-header-title">{t("adminDashboard.title")}</h2>
          <div className="admin-dashboard-header-actions">
            {/* Language Toggle */}
            <button
              onClick={() => {
                const newLang = i18n.language === "en" ? "ar" : "en";
                i18n.changeLanguage(newLang);
              }}
              className="admin-dashboard-language-toggle"
              title={
                i18n.language === "en"
                  ? t("language.switchToArabic")
                  : t("language.switchToEnglish")
              }
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
              </svg>
              <span>{i18n.language === "en" ? "AR" : "EN"}</span>
            </button>
            {getUserType() === 'ADMIN' && (
              <button className="admin-dashboard-stats-btn" onClick={() => navigate('/statistics')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="20" x2="18" y2="10"></line>
                  <line x1="12" y1="20" x2="12" y2="4"></line>
                  <line x1="6" y1="20" x2="6" y2="14"></line>
                </svg>
                <span>{t("adminDashboard.statistics")}</span>
              </button>
            )}
            <button className="admin-dashboard-logout-btn" onClick={handleLogout}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
              <span>{t("adminDashboard.logout")}</span>
            </button>
          </div>
        </div>
      </div>

      <div className="admin-dashboard-layout">
        {/* Left Sidebar Menu */}
        <aside className="admin-dashboard-sidebar">
          <nav className="admin-dashboard-sidebar-nav">
            {getUserType() === 'ADMIN' && (
              <>
                <button
                  className={`admin-dashboard-sidebar-item ${filter === "all" ? "active" : ""}`}
                  onClick={() => navigate("/admin/dashboard")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="9" y1="3" x2="9" y2="21"></line>
                    <line x1="3" y1="9" x2="21" y2="9"></line>
                  </svg>
                  <span>{t("adminDashboard.menu.all")}</span>
                </button>
                <button
                  className={`admin-dashboard-sidebar-item ${filter === "verified" ? "active" : ""}`}
                  onClick={() => navigate("/admin/dashboard/clinics/verified")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                  <span>{t("adminDashboard.menu.verified")}</span>
                </button>
                <button
                  className={`admin-dashboard-sidebar-item ${filter === "rejected" ? "active" : ""}`}
                  onClick={() => navigate("/admin/dashboard/clinics/rejected")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="15" y1="9" x2="9" y2="15"></line>
                    <line x1="9" y1="9" x2="15" y2="15"></line>
                  </svg>
                  <span>{t("adminDashboard.menu.rejected")}</span>
                </button>
                <button
                  className={`admin-dashboard-sidebar-item ${filter === "new" ? "active" : ""}`}
                  onClick={() => navigate("/admin/dashboard/clinics/new")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="16"></line>
                    <line x1="8" y1="12" x2="16" y2="12"></line>
                  </svg>
                  <span>{t("adminDashboard.menu.new")}</span>
                </button>
                <button
                  className={`admin-dashboard-sidebar-item ${filter === "upgrade-requests" ? "active" : ""}`}
                  onClick={() => navigate("/admin/dashboard/clinics/upgrade-requests")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                  <span>{t("adminDashboard.menu.upgradeRequests")}</span>
                </button>
                <button
                  className={`admin-dashboard-sidebar-item ${filter === "paid" ? "active" : ""}`}
                  onClick={() => navigate("/admin/dashboard/paid")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  <span>{t("adminDashboard.menu.paid")}</span>
                </button>
                <button
                  className={`admin-dashboard-sidebar-item ${filter === "announcements" ? "active" : ""}`}
                  onClick={() => navigate("/admin/dashboard/announcements")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                  </svg>
                  <span>{t("adminDashboard.menu.announcements", "Announcements")}</span>
                </button>
                <button
                  className={`admin-dashboard-sidebar-item ${filter === "bulk_messages" ? "active" : ""}`}
                  onClick={() => navigate("/admin/dashboard/bulk-messages")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                  <span>{t("adminDashboard.menu.bulkMessages", "Bulk Messaging")}</span>
                </button>
              </>
            )}

            {/* CMS Section - Accessible to both Admin and Content Admin */}
            {(getUserType() === 'CONTENT_ADMIN' || getUserType() === 'ADMIN') && (
              <div className="admin-dashboard-sidebar-group">
                <button
                  className={`admin-dashboard-sidebar-item ${filter.startsWith("cms_") ? "active" : ""}`}
                  onClick={() => setIsCmsOpen(!isCmsOpen)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
                  </svg>
                  <span>{t("adminDashboard.menu.cms", "CMS")}</span>
                  <svg
                    className={`chevron ${isCmsOpen ? "rotated" : ""}`}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>

              {isCmsOpen && (
                <div className="admin-dashboard-sidebar-sub">
                  <div className="admin-dashboard-sidebar-group">
                    <button
                      className={`admin-dashboard-sidebar-item ${filter === "cms_banners" ? "active" : ""}`}
                      onClick={() => setIsBannerSubOpen(!isBannerSubOpen)}                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                      </svg>
                      <span>{t("adminDashboard.menu.cms_banners", "App Main Banner")}</span>
                      <svg
                        className={`chevron ${isBannerSubOpen ? "rotated" : ""}`}
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </button>
                    {isBannerSubOpen && (
                      <div className="admin-dashboard-sidebar-sub-nested">
                        <button
                          className={`admin-dashboard-sidebar-item ${filter === "cms_banners" ? "active" : ""}`}
                          onClick={() => navigate("/admin/dashboard/cms/banners")}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="8" y1="6" x2="21" y2="6"></line>
                            <line x1="8" y1="12" x2="21" y2="12"></line>
                            <line x1="8" y1="18" x2="21" y2="18"></line>
                            <line x1="3" y1="6" x2="3.01" y2="6"></line>
                            <line x1="3" y1="12" x2="3.01" y2="12"></line>
                            <line x1="3" y1="18" x2="3.01" y2="18"></line>
                          </svg>
                          <span>{t("adminDashboard.menu.banners_list_data", "Banners Data")}</span>
                        </button>
                        <button
                          className={`admin-dashboard-sidebar-item ${filter === "cms_banners_add" ? "active" : ""}`}
                          onClick={() => navigate("/admin/dashboard/cms/banners/add")}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                          <span>{t("adminDashboard.menu.add_breed_data", "Add Banners Data")}</span>
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Species Sub-section */}
                  <div className="admin-dashboard-sidebar-group">
                    <button
                      className={`admin-dashboard-sidebar-item ${filter.startsWith("cms_species") ? "active" : ""}`}
                      onClick={() => setIsSpeciesSubOpen(!isSpeciesSubOpen)}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <ellipse cx="12" cy="12" rx="10" ry="6"></ellipse>
                        <path d="M2 12c0 4 4.5 7 10 7s10-3 10-7"></path>
                        <path d="M12 5v14"></path>
                      </svg>
                      <span>{t("adminDashboard.menu.cms_species", "Species")}</span>
                      <svg
                        className={`chevron ${isSpeciesSubOpen ? "rotated" : ""}`}
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </button>

                    {isSpeciesSubOpen && (
                      <div className="admin-dashboard-sidebar-sub-nested">
                        <button
                          className={`admin-dashboard-sidebar-item ${filter === "cms_species_list" ? "active" : ""}`}
                          onClick={() => navigate("/admin/dashboard/cms/species")}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="8" y1="6" x2="21" y2="6"></line>
                            <line x1="8" y1="12" x2="21" y2="12"></line>
                            <line x1="8" y1="18" x2="21" y2="18"></line>
                            <line x1="3" y1="6" x2="3.01" y2="6"></line>
                            <line x1="3" y1="12" x2="3.01" y2="12"></line>
                            <line x1="3" y1="18" x2="3.01" y2="18"></line>
                          </svg>
                          <span>{t("adminDashboard.menu.species_list_data", "Species List")}</span>
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="admin-dashboard-sidebar-group">
                    <button
                      className={`admin-dashboard-sidebar-item ${filter.startsWith("cms_breeds") ? "active" : ""}`}
                      onClick={() => setIsBreedsSubOpen(!isBreedsSubOpen)}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                        <line x1="7" y1="7" x2="7.01" y2="7"></line>
                      </svg>
                      <span>{t("adminDashboard.menu.cms_breeds", "Breeds")}</span>
                      <svg
                        className={`chevron ${isBreedsSubOpen ? "rotated" : ""}`}
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </button>

                    {isBreedsSubOpen && (
                      <div className="admin-dashboard-sidebar-sub-nested">
                        <button
                          className={`admin-dashboard-sidebar-item ${filter === "cms_breeds_list" ? "active" : ""}`}
                          onClick={() => navigate("/admin/dashboard/cms/breeds")}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="8" y1="6" x2="21" y2="6"></line>
                            <line x1="8" y1="12" x2="21" y2="12"></line>
                            <line x1="8" y1="18" x2="21" y2="18"></line>
                            <line x1="3" y1="6" x2="3.01" y2="6"></line>
                            <line x1="3" y1="12" x2="3.01" y2="12"></line>
                            <line x1="3" y1="18" x2="3.01" y2="18"></line>
                          </svg>
                          <span>{t("adminDashboard.menu.breeds_list_data", "Breeds Data")}</span>
                        </button>
                        <button
                          className={`admin-dashboard-sidebar-item ${filter === "cms_breeds_add" ? "active" : ""}`}
                          onClick={() => navigate("/admin/dashboard/cms/breeds/add")}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                          <span>{t("adminDashboard.menu.add_breed_data", "Add Breed Data")}</span>
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="admin-dashboard-sidebar-group">
                    <button
                      className={`admin-dashboard-sidebar-item ${filter.startsWith("cms_ads") ? "active" : ""}`}
                      onClick={() => setIsAdsSubOpen(!isAdsSubOpen)}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                        <line x1="8" y1="21" x2="16" y2="21"></line>
                        <line x1="12" y1="17" x2="12" y2="21"></line>
                        <path d="M7 8h10M7 12h5"></path>
                      </svg>
                      <span>{t("adminDashboard.menu.cms_advertisements", "Advertisements")}</span>
                      <svg
                        className={`chevron ${isAdsSubOpen ? "rotated" : ""}`}
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </button>

                    {isAdsSubOpen && (
                      <div className="admin-dashboard-sidebar-sub-nested">
                        <button
                          className={`admin-dashboard-sidebar-item ${filter === "cms_ads_list" ? "active" : ""}`}
                          onClick={() => navigate("/admin/dashboard/cms/advertisements")}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="8" y1="6" x2="21" y2="6"></line>
                            <line x1="8" y1="12" x2="21" y2="12"></line>
                            <line x1="8" y1="18" x2="21" y2="18"></line>
                            <line x1="3" y1="6" x2="3.01" y2="6"></line>
                            <line x1="3" y1="12" x2="3.01" y2="12"></line>
                            <line x1="3" y1="18" x2="3.01" y2="18"></line>
                          </svg>
                          <span>{t("adminDashboard.menu.ads_list_data", "Advertisements Data")}</span>
                        </button>
                        <button
                          className={`admin-dashboard-sidebar-item ${filter === "cms_ads_add" ? "active" : ""}`}
                          onClick={() => navigate("/admin/dashboard/cms/advertisements/add")}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                          </svg>
                          <span>{t("adminDashboard.menu.add_ad_data", "Add Advertisement")}</span>
                        </button>
                      </div>
                    )}
                  </div>

                </div>
              )}
                </div>
              )}
            </nav>
          </aside>

        {/* Main Content */}
        <div className="admin-dashboard-container">
          {filter === "announcements" ? (
            <SystemAnnouncementComposer />
          ) : filter === "bulk_messages" ? (
            <BulkMessageComposer />
          ) : filter.startsWith("cms_banners") ? (
            <AppBannerManager initialView={filter === "cms_banners_add" ? "add" : filter === "cms_banners_edit" ? "edit" : "list"} />
          ) : filter.startsWith("cms_breeds") ? (
            <BreedManager initialView={filter === "cms_breeds_add" ? "form" : "list"} />
          ) : filter.startsWith("cms_ads") ? (
            <AdvertisementManager initialView={(filter === "cms_ads_add" || filter === "cms_ads_edit") ? "form" : "summary"} />
          ) : filter.startsWith("cms_species") ? (
            <SpeciesManager initialView={filter === "cms_species_add" ? "form" : "list"} />
          ) : (
            <>
              <div className="admin-dashboard-header">
                <div className="admin-dashboard-stats">
                  <div className="admin-dashboard-stat">
                    <span className="admin-dashboard-stat-label">{t("adminDashboard.stats.unverified")}</span>
                    <span className="admin-dashboard-stat-value">
                      {totalClinics.filter(c => c.status === 'unverified').length}
                    </span>
                  </div>
                  <div className="admin-dashboard-stat">
                    <span className="admin-dashboard-stat-label">{t("adminDashboard.stats.verified")}</span>
                    <span className="admin-dashboard-stat-value">
                      {totalClinics.filter(c => c.status === 'verified').length}
                    </span>
                  </div>
                  <div className="admin-dashboard-stat">
                    <span className="admin-dashboard-stat-label">{t("adminDashboard.stats.rejected")}</span>
                    <span className="admin-dashboard-stat-value">
                      {totalClinics.filter(c => c.status === 'rejected').length}
                    </span>
                  </div>
                  <div className="admin-dashboard-stat">
                    <span className="admin-dashboard-stat-label">{t("adminDashboard.stats.total")}</span>
                    <span className="admin-dashboard-stat-value">{totalClinics.length}</span>
                  </div>
                </div>
              </div>

              <div className="admin-dashboard-controls">
                <div className="admin-dashboard-search">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <path d="m21 21-4.35-4.35"></path>
                  </svg>
                  <input
                    type="text"
                    placeholder={t("adminDashboard.searchPlaceholder")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="admin-dashboard-search-input"
                  />
                </div>
              </div>

              <div className="admin-dashboard-clinics">
                {filteredClinics.length > 0 ? (
                  filteredClinics.map(clinic => (
                    <ClinicReviewCard
                      key={clinic.id}
                      clinic={clinic}
                      onViewDetails={handleViewDetails}
                      onVerify={handleVerifyClinic}
                      onReject={handleRejectClinic}
                      onUpgradeSubscription={handleUpgradeSubscription}
                      onDowngradeSubscription={handleDowngradeSubscription}
                    />
                  ))
                ) : (
                  <div className="admin-dashboard-empty">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="9" y1="3" x2="9" y2="21"></line>
                    </svg>
                    <h3>{t("adminDashboard.empty.title")}</h3>
                    <p>
                      {filter === "new"
                        ? t("adminDashboard.empty.new")
                        : filter === "verified"
                          ? t("adminDashboard.empty.verified")
                          : filter === "rejected"
                            ? t("adminDashboard.empty.rejected")
                            : filter === "upgrade-requests"
                              ? t("adminDashboard.empty.upgradeRequests")
                              : filter === "paid"
                                ? t("adminDashboard.empty.paid")
                                : t("adminDashboard.empty.noMatch")}
                    </p>
                  </div>
                )}
              </div>

              {allClinics.length > clinicsPerPage && (
                <div className="admin-dashboard-pagination">
                  <button
                    className="admin-dashboard-pagination-btn"
                    onClick={() => {
                      const nextPage = Math.max(1, currentPage - 1);
                      if (nextPage !== currentPage) {
                        setCurrentPage(nextPage);
                        scrollToTop();
                      }
                    }}
                    disabled={currentPage === 1}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                    {t("adminDashboard.pagination.previous")}
                  </button>

                  <div className="admin-dashboard-pagination-info">
                    <span>
                      {t("adminDashboard.pagination.page", { current: currentPage, total: Math.ceil(allClinics.length / clinicsPerPage) })}
                    </span>
                    <span className="admin-dashboard-pagination-count">
                      {t("adminDashboard.pagination.showing", {
                        start: ((currentPage - 1) * clinicsPerPage) + 1,
                        end: Math.min(currentPage * clinicsPerPage, allClinics.length),
                        total: allClinics.length
                      })}
                    </span>
                  </div>

                  <button
                    className="admin-dashboard-pagination-btn"
                    onClick={() => {
                      const nextPage = Math.min(Math.ceil(allClinics.length / clinicsPerPage), currentPage + 1);
                      if (nextPage !== currentPage) {
                        setCurrentPage(nextPage);
                        scrollToTop();
                      }
                    }}
                    disabled={currentPage >= Math.ceil(allClinics.length / clinicsPerPage)}
                  >
                    {t("adminDashboard.pagination.next")}
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showDetailModal && selectedClinic && (
        <ClinicDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedClinic(null);
          }}
          clinic={selectedClinic}
          onVerify={handleVerifyClinic}
          onReject={handleRejectClinic}
          onUpgradeSubscription={handleUpgradeSubscription}
          onDowngradeSubscription={handleDowngradeSubscription}
        />
      )}
    </div>
  );
};

export default AdminDashboard;

